<template>
  <div ref="resizableBox" class="kk__table">
    <div
      ref="customTable"
      class="table-container"
      role="cusTable"
      tabindex="0"
      :style="{ overflow: overflowValue }"
      @focusin="setScope"
      @focusout="unSetScope"
    >
      <div ref="scrollWrapper" class="scroll-wrapper">
        <table>
          <!-- Table Header -->
          <thead :style="{ ...headStyle }">
            <tr ref="tableHeaderRow">
              <th
                v-for="(header, i) in localHeaders"
                :key="i"
                class="thtdBorder"
                :style="{ width: header.width + 'px', minWidth: header.accessor === 'checkbox' ? '30px' : '0' }"
              >
                <template v-if="header.accessor === 'checkbox'">
                  <checkbox :item="header" @click="selectAll" />
                </template>
                <template v-else-if="header.width > 10">
                  <span class="header-content">
                    {{ header.label }}
                    <i
                      v-if="header.icon"
                      :class="header.icon"
                      class="filter-trigger"
                      @click.stop="toggleDropdown(i)"
                    ></i>
                    <!-- Dropdown menu -->
                    <div v-if="activeDropdown === i" class="filter-dropdown">
                      <div class="dropdown-option"><input type="checkbox" /> Option</div>
                      <div class="dropdown-option"><input type="checkbox" /> Option</div>
                      <div class="dropdown-option"><input type="checkbox" /> Option</div>
                    </div>
                  </span>
                </template>

                <div
                  v-if="header.resizable !== false && header.accessor !== 'checkbox'"
                  class="resize-handle"
                  @mousedown="initResize($event, i)"
                ></div>
              </th>
            </tr>
          </thead>

          <!-- Table Body -->
          <tbody>
            <tr
              v-for="(row, i) in data"
              :key="i"
              :class="{
                'item-active': i === index,
                selected: selectedItems?.includes(row),
                ctrlNavigation: i === ctrlIndex,
              }"
              :style="i === index || hoverIndex === i ? tdBg : {}"
              @click="selectItem($event, row, i)"
              @mouseover="hoverIndex = i"
              @mouseleave="hoverIndex = null"
            >
              <td
                v-for="(header, j) in localHeaders"
                :key="j"
                :title="row[header.accessor]?.value"
                :style="{ width: header.width + 'px', minWidth: header.accessor === 'checkbox' ? '40px' : '0' }"
              >
                <template v-if="header.accessor === 'checkbox'">
                  <checkbox :item="row" :is-checked="row.selected" @passItem="selectCheckbox($event, row, i)" />
                </template>
                <template v-else-if="header.width > 10">
                  <span>
                    <template v-if="row[header.accessor]?.icon">
                      <i :class="icons[row[header.accessor]?.icon]"></i>
                    </template>
                    <template v-else>
                      <span class="td__text">
                        {{ row[header.accessor]?.value || row[header.accessor] }}
                      </span>
                    </template>
                  </span>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import hotkeys from "hotkeys-js";
import { HotkeyScopes } from "../../utils/constants";
import Checkbox from "./Checkbox.vue";
import { icons } from "../../utils/icons";

export default {
  components: { Checkbox },
  props: ["headers", "data", "headStyle", "tdBg", "parentScope", "overflowValue"],
  data() {
    return {
      activeDropdown: null,
      headerTabIndex: null,
      ctrlIndex: null,
      selectedItems: [],
      shiftSelection: 0,
      hoverIndex: null,
      index: -1,
      previousScope: null,
      resizing: false,
      startX: 0,
      startWidth: 0,
      resizeColIndex: null,
      localHeaders: [],
      storageKey: "customTableColumnWidths",
      MIN_WIDTH: 50,
      icons: icons,
    };
  },
  mounted() {
    const savedWidths = JSON.parse(localStorage.getItem(this.storageKey) || "null");

    this.localHeaders = this.headers.map((h, index) => {
      const isCheckbox = h.accessor === "checkbox";
      const width = isCheckbox && index === 0 ? 40 : savedWidths?.[index] ?? (h.width || 120);
      return {
        ...h,
        width,
        resizable: !isCheckbox && h.resizable !== false,
      };
    });

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === this.$refs.resizableBox) {
          const container = this.$refs.resizableBox;
          if (!container) return;

          const headerRow = this.$refs.tableHeaderRow;
          if (!headerRow) return;

          const thElements = headerRow.querySelectorAll("th");
          if (!thElements.length) return;

          requestAnimationFrame(() => {
            const totalContainerWidth = container.clientWidth;

            const totalOtherColsWidth = Array.from(thElements)
              .slice(0, -1)
              .reduce((sum, th) => sum + th.getBoundingClientRect().width, 0);

            const lastColIndex = this.localHeaders.length - 1;
            if (this.localHeaders[lastColIndex].accessor === "checkbox") return; // don't resize checkbox col

            const remainingWidth = totalContainerWidth - totalOtherColsWidth;
            const finalWidth = Math.max(remainingWidth - 5, this.MIN_WIDTH);

            const updatedHeaders = [...this.localHeaders];
            updatedHeaders[lastColIndex] = {
              ...updatedHeaders[lastColIndex],
              width: finalWidth,
            };
            this.localHeaders = updatedHeaders;
            this.saveColumnWidths();
          });
        }
      }
    });

    if (this.$refs.resizableBox) {
      this.resizeObserver.observe(this.$refs.resizableBox);
    }
    this.$refs.customTable.addEventListener("scroll", this.updateClipPath);
  },
  beforeUnmount() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    document.body.style.cursor = "";
    this.$refs.customTable.removeEventListener("scroll", this.updateClipPath);
  },
  methods: {
    toggleDropdown(index) {
      this.activeDropdown = this.activeDropdown === index ? null : index;
      this.headerTabIndex = index;
    },
    handleShiftTab(event) {
      if (event.key === "Tab" && event.shiftKey) {
        this.activeDropdown = null;
        event.preventDefault();
        if (this.headerTabIndex <= 0) {
          this.headerTabIndex = null;
          this.$emit("handleTab", event.shiftKey);
        } else {
          this.headerTabIndex--;
        }
      }
    },
    setItem(item, i) {
      this.index = i;
      this.$emit("setItem", item);
    },
    selectAll() {
      this.$emit("selectAll");
    },
    setScope() {
      this.previousScope = hotkeys.getScope();
      this.$store.dispatch("setScope", HotkeyScopes.CustomTable);

      hotkeys(
        "Up, Down, shift+up, shift+down,ctrl+up, ctrl+down ,Enter, Delete, ctrl+Space,space,tab,alt+down,alt+up",
        HotkeyScopes.CustomTable,
        (event) => {
          event.preventDefault();

          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            const direction = event.key === "ArrowDown" ? 1 : -1;
            if (event.ctrlKey) {
              this.navigateWithCtrl(direction);
            } else if (event.shiftKey) {
              this.shiftSelect(direction);
              this.ctrlIndex = null;
            } else if (event.altKey) {
              if (event.key == "ArrowDown") {
                this.activeDropdown = this.headerTabIndex;
              } else {
                this.activeDropdown = null;
              }
            } else {
              this.navigateSelection(direction);
              this.ctrlIndex = null;
            }
          } else if (event.code === "Space" && event.ctrlKey) {
            this.addItemToList(this.data[this.ctrlIndex]);
          }
        }
      );

      this.headerTabIndex = 0;

      hotkeys("tab", HotkeyScopes.CustomTable, (event) => {
        event.preventDefault();
        this.activeDropdown = null;
        if (this.headerTabIndex <= this.headers.length - 1) {
          if (this.headerTabIndex == this.headers.length - 1) {
            this.setIndexAndScroll();
          }
          this.headerTabIndex++;
        } else {
          this.headerTabIndex = null;
          this.$emit("handleTab");
          this.unSetScope();
        }
      });

      hotkeys("space", HotkeyScopes.CustomTable, (event) => {
        event.preventDefault();
        this.selectCheckbox(undefined, this.data[this.index], this.index);
      });

      this.$refs.customTable.addEventListener("keydown", this.handleShiftTab);

      setTimeout(() => {
        if (this.index === -1) {
          this.index = 0;
          this.scrollToIndex();
        }
      }, 300);
    },
    unSetScope() {
      this.index = -1;
      this.$store.dispatch("setScope", this.parentScope);
      hotkeys.unbind(
        "Up, Down, shift+up, shift+down,ctrl+up, ctrl+down ,Enter, Delete, ctrl+Space, space,tab,alt+down,alt+up",
        HotkeyScopes.CustomTable
      );
      if (this.$refs.customTable) {
        this.$refs.customTable.removeEventListener("keydown", this.handleShiftTab);
      }
    },
    addItemToList(item) {
      const itemIndex = this.selectedItems.indexOf(item);
      if (itemIndex === -1) {
        this.selectedItems.push(item);
        this.index = this.ctrlIndex;
      } else {
        this.selectedItems.splice(itemIndex, 1);
        this.index = this.data.indexOf(this.selectedItems[this.selectedItems.length - 1]);
      }
    },
    navigateWithCtrl(direction) {
      if (this.ctrlIndex == null) this.ctrlIndex = this.index;
      this.ctrlIndex = (this.ctrlIndex + direction) % this.data.length;
      if (this.ctrlIndex < 0) this.ctrlIndex = this.data.length - 1;
      this.scrollToIndex(this.ctrlIndex);
    },
    shiftSelect(direction) {
      if ((direction < 0 && this.index == 0) || (direction > 0 && this.index == this.data.length - 1)) return;
      this.index += direction;
      if (!this.selectedItems.includes(this.data[this.index])) {
        this.selectedItems.push(this.data[this.index]);
      } else {
        this.selectedItems.pop();
      }
      console.log(this.selectedItems);

      this.scrollToIndex(this.index);
    },
    navigateSelection(direction) {
      this.index = (this.index + direction) % this.data.length;
      if (this.index < 0) this.index = this.data.length - 1;
      this.selectedItems = [this.data[this.index]];
      this.scrollToIndex(this.index);
    },
    updateClipPath() {
      const scrollTop = this.$refs.customTable.scrollTop;
      const elementToHide = document.querySelector("tbody");
      elementToHide.style.clipPath = `inset(${scrollTop}px 0 0 0)`;
    },
    setIndexAndScroll() {
      this.index = 0;
      this.selectedItems = [this.data[this.index]];
      this.scrollToIndex(this.index);
    },
    scrollToIndex() {
      const item = document.querySelector(".item-active");
      if (this.index === 0) {
        this.$refs.customTable.scrollTop = 0;
      } else if (this.index === this.data.length - 1) {
        this.$refs.customTable.scrollTop = this.$refs.customTable.scrollHeight;
      } else {
        item?.scrollIntoView({
          behavior: "auto",
          block: "center",
        });
      }
    },

    selectCheckbox(event, item, i) {
      this.headerTabIndex = this.headers.length;
      this.index = i;
      this.selectedItems = [item];
      this.$emit("passItem", item);
    },

    selectItem(event, item, i) {
      this.activeDropdown = null;

      if (event?.ctrlKey || event?.metaKey) {
        this.addItemToList(item);
      } else if (event?.shiftKey && this.index !== null) {
        const itemCount = Math.abs(i - this.index);
        const direction = i > this.index ? 1 : -1;

        for (let i = 0; i < itemCount; i++) {
          this.shiftSelect(direction);
        }
      } else {
        this.headerTabIndex = this.headers.length;
        this.index = i;
        this.selectedItems = [item];
      }
    },

    initResize(event, index) {
      if (this.localHeaders[index].accessor === "checkbox") return;

      this.resizing = true;
      this.startX = event.clientX;
      this.startWidth = this.localHeaders[index].width;
      this.resizeColIndex = index;

      document.body.style.cursor = "col-resize";

      window.addEventListener("mousemove", this.onResize);
      window.addEventListener("mouseup", this.stopResize);
    },
    onResize(event) {
      if (!this.resizing) return;

      const delta = event.clientX - this.startX;
      const newWidth = this.startWidth + delta;
      this.localHeaders[this.resizeColIndex].width = Math.max(newWidth, this.MIN_WIDTH);
      this.saveColumnWidths();
    },
    stopResize() {
      this.resizing = false;
      document.body.style.cursor = "";
      window.removeEventListener("mousemove", this.onResize);
      window.removeEventListener("mouseup", this.stopResize);
    },
    saveColumnWidths() {
      const widths = this.localHeaders.map((h) => h.width);
      localStorage.setItem(this.storageKey, JSON.stringify(widths));
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../assets/styles/variables.less";
.header-content {
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
}

.filter-trigger {
  cursor: pointer;
}

.filter-trigger:hover {
  opacity: 0.8;
}

.filter-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  width: 100px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.dropdown-option {
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.dropdown-option:hover {
  background-color: #f5f5f5;
}

.dropdown-option:hover {
  background-color: #f5f5f5;
}

tr.selected {
  background-color: #cce5ff;
}
tr.ctrlNavigation {
  border: 1px dotted black;
}

.kk__table {
  color: @kk-color-primary-blue;
}

.headerBorder {
  background-color: #cce5ff;
}

.thtdBorder {
  span {
    padding-left: 3px;
    justify-content: space-between;
    i {
      margin-right: 3px;
    }
  }
}
.table-container {
  height: 16rem;
  width: 27rem;
  min-height: 10rem;
  min-width: 10rem;
  position: relative;
}

.scroll-wrapper {
  min-width: max-content;
}

.thtdBorder:not(:last-child) {
  border-right: 1px solid rgba(8, 50, 119, 0.13);
}

label {
  display: flex;
}

.kk__table,
.table-container {
  &:focus-within {
    border: none;
    outline: none;
  }
}

table {
  border-collapse: collapse;
}

thead {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: white;
}

th,
td {
  padding: 5px 3px;
  text-align: left;
}

th {
  position: relative;
  font-weight: bold;

  span {
    i {
      background: #f4f6f9;
      border-radius: 3px;
      font-size: 1.3rem;
      margin-left: auto;
    }
  }
}

td span,
th span {
  display: flex;
  align-items: center;
}

td label,
th label {
  margin: 0 10px;
}

.td__text {
  display: inline-block;
  max-width: 17ch;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}

.resize-handle {
  position: absolute;
  right: 0;
  top: 0;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  z-index: 10;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
}
</style>
